/**
 * ToastManager - Displays temporary notification messages on screen
 */

interface Toast {
  message: string;
  color: string;
  duration: number;
  elapsed: number;
  opacity: number;
}

export class ToastManager {
  private toasts: Toast[] = [];
  private readonly maxToasts = 5;
  private readonly fadeInDuration = 0.2;
  private readonly fadeOutDuration = 0.4;

  /** Show a toast notification */
  show(message: string, duration: number = 2.5, color: string = '#4ecca3'): void {
    // Remove oldest if at max
    if (this.toasts.length >= this.maxToasts) {
      this.toasts.shift();
    }

    this.toasts.push({
      message,
      color,
      duration,
      elapsed: 0,
      opacity: 0
    });
  }

  /** Show a success toast (green) */
  success(message: string, duration: number = 2.5): void {
    this.show(message, duration, '#4ecca3');
  }

  /** Show an info toast (blue) */
  info(message: string, duration: number = 2.5): void {
    this.show(message, duration, '#3498db');
  }

  /** Show an error toast (red) */
  error(message: string, duration: number = 3): void {
    this.show(message, duration, '#e74c3c');
  }

  /** Update toast timers */
  update(deltaTime: number): void {
    for (let i = this.toasts.length - 1; i >= 0; i--) {
      const toast = this.toasts[i];
      if (!toast) continue;

      toast.elapsed += deltaTime;

      // Calculate opacity based on phase
      if (toast.elapsed < this.fadeInDuration) {
        // Fade in
        toast.opacity = toast.elapsed / this.fadeInDuration;
      } else if (toast.elapsed < toast.duration - this.fadeOutDuration) {
        // Fully visible
        toast.opacity = 1;
      } else if (toast.elapsed < toast.duration) {
        // Fade out
        const fadeOutProgress = (toast.duration - toast.elapsed) / this.fadeOutDuration;
        toast.opacity = fadeOutProgress;
      } else {
        // Remove expired toast
        this.toasts.splice(i, 1);
      }
    }
  }

  /** Render all active toasts */
  render(ctx: CanvasRenderingContext2D, screenWidth: number, _screenHeight: number): void {
    void _screenHeight; // Unused but kept for consistent API

    const padding = 15;
    const toastHeight = 36;
    const toastSpacing = 8;
    const rightMargin = 20;
    const topMargin = 60; // Below time display

    ctx.font = '14px monospace';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';

    for (let i = 0; i < this.toasts.length; i++) {
      const toast = this.toasts[i];
      if (!toast) continue;

      const y = topMargin + i * (toastHeight + toastSpacing);

      // Measure text width
      const textWidth = ctx.measureText(toast.message).width;
      const toastWidth = textWidth + padding * 2;
      const x = screenWidth - rightMargin - toastWidth;

      // Background with opacity
      ctx.fillStyle = `rgba(20, 20, 40, ${0.9 * toast.opacity})`;
      ctx.beginPath();
      ctx.roundRect(x, y, toastWidth, toastHeight, 6);
      ctx.fill();

      // Border
      ctx.strokeStyle = toast.color;
      ctx.globalAlpha = toast.opacity;
      ctx.lineWidth = 2;
      ctx.stroke();

      // Text
      ctx.fillStyle = toast.color;
      ctx.fillText(toast.message, screenWidth - rightMargin - padding, y + toastHeight / 2);

      ctx.globalAlpha = 1;
    }
  }
}
